import store from '@/store'
import { omit, isEmpty, isString, isNumber, get } from 'lodash-es'
import { joinWithBr } from '@/utils/html'
import { formatDate } from '@science-data-experts/formatters'

const buildDescription = (descriptionData, { avoidKeys = ['name'] } = {}) => {
  const coreDescriptionData = omit(descriptionData, avoidKeys)

  if (isEmpty(coreDescriptionData)) {
    return null
  }

  const descriptionsField = [
    { field: 'description' },
    { field: 'compiledAt', prefix: 'Compiled at:', formatter: formatDate },
    { field: 'version', prefix: 'Version:' },
    { field: 'buildId', prefix: 'Build ID:' },
  ]

  const formatValueWithPrefix = (value, prefix) =>
    (!isString(value) || isEmpty(value)) && !isNumber(value) ? null : `${isString(prefix) ? `${prefix} ` : ''}${value}`

  const descriptionArray = descriptionsField.map(({ field, formatter, prefix = null }) => {
    const fieldValue = get(coreDescriptionData, field)
    const formattedValue = formatter ? formatter(fieldValue) : fieldValue
    return formatValueWithPrefix(formattedValue, prefix)
  })

  return joinWithBr(descriptionArray)
}

const getMainMetadata = ({
  name = null,
  compiledAt = null,
  description = null,
  version = null,
  buildId = null,
} = {}) => {
  return { name, compiledAt, description, version, buildId }
}

const createMetadataHelpers = (storeGettersKey) => {
  const getMetadata = () => store.getters[storeGettersKey]

  const getEndpoints = () => getMetadata()?.endpoints || []

  const getVersionMetadata = () => getMetadata()?.version

  const getEndpointIds = () => getEndpoints().map((endpoint) => endpoint?.id)

  const getEndpoint = (modelId) => {
    const endpoints = getEndpoints()
    const modelMeta = endpoints.find((endpoint) => String(endpoint.id) === String(modelId))
    if (!modelMeta) console.warn(`No metadata found for ${modelId}.`)
    return modelMeta || null
  }

  const getName = (modelId) => getEndpoint(modelId)?.name ?? modelId

  const getModelDescription = (modelId) => getEndpoint(modelId)?.description ?? null

  const getDescription = (modelId) => {
    const versionMetadata = getVersionMetadata()
    const { name, compiled: compiledAt, description, version, build_id: buildId } = versionMetadata || {}
    const mainMetadata = getMainMetadata({ name, compiledAt, description, version, buildId })
    const modelDescription = getModelDescription(modelId)

    return buildDescription({ ...mainMetadata, description: modelDescription })
  }

  return {
    getEndpointIds,
    getName,
    getDescription,
  }
}

export { createMetadataHelpers, getMainMetadata, buildDescription }
